import {
    AbfLoanExpanded,
    summarizeLedgerAccount,
    calculateHealthRatio,
    calculateLoanHealth,
    getZcLoanCollateral,
    getZcLedger,
    getZcLoanLiquidationThreshold,
    BsMetaUtil,
    calculateCurrentLtv,
    getZcLoanLtv,
    getHealthStatus,
    getLoanCollateralUsdValue
} from "@bridgesplit/abf-react";
import { bsMath } from "@bridgesplit/utils";
import { TokenListMetadata } from "@bridgesplit/abf-sdk";

import { RefinanceOption } from "./RefinanceLoan";
import { CollateralChange, CollateralLoanHealthChange } from "./types";

// Function to get the option label for Select components
export function getOptionLabel(option: RefinanceOption) {
    return {
        disabled: !option.bestQuote,
        value: option.strategyDuration,
        label: option
    };
}

export function calculateNewLoanHealth(
    loanExpanded: AbfLoanExpanded | undefined,
    collateralChanges: CollateralChange[] | undefined
): CollateralLoanHealthChange | undefined {
    const ledgerAccount = getZcLedger(loanExpanded);
    const strategyLtv = getZcLoanLtv(loanExpanded);
    if (!loanExpanded) return undefined;

    const originalCollateralValueUsd = getLoanCollateralUsdValue(loanExpanded);

    // Return early if no changes to avoid unnecessary calculations
    if (!collateralChanges?.length) {
        const { health: originalHealth, ltv, liquidationThreshold } = calculateLoanHealth(loanExpanded);
        const originalHealthStatus = getHealthStatus(ltv, strategyLtv, liquidationThreshold);
        return {
            newHealth: originalHealth,
            originalHealth,
            originalCollateralValueUsd,
            newLoanCollateralValueUsd: originalCollateralValueUsd,
            strategyLtv: strategyLtv ?? 0,
            liquidationThreshold,
            originalHealthStatus,
            newHealthStatus: originalHealthStatus
        };
    }

    const { totalOutstanding } = summarizeLedgerAccount(ledgerAccount);
    const collateralInfo = getZcLoanCollateral(loanExpanded);

    let newLoanCollateralValueUsd = 0;

    for (const { change } of collateralChanges) {
        const newCollateralAmount = (collateralInfo?.amount ?? 0) + change;
        const newUsdCollateralValue = bsMath.mul(newCollateralAmount, collateralInfo?.usdPrice);
        newLoanCollateralValueUsd += newUsdCollateralValue ?? 0;
    }

    const totalOwedValue = bsMath.mul(totalOutstanding, loanExpanded.principalUsdPrice ?? 0);

    const { health: originalHealth } = calculateLoanHealth(loanExpanded);

    const liquidationThreshold = getZcLoanLiquidationThreshold(loanExpanded);

    const newHealth = calculateHealthRatio(totalOwedValue, newLoanCollateralValueUsd, liquidationThreshold);
    const newLtv = calculateCurrentLtv({ totalOwedValue, loanCollateralValue: newLoanCollateralValueUsd });
    const originalLtv = calculateCurrentLtv({ totalOwedValue, loanCollateralValue: originalCollateralValueUsd });

    const result = {
        strategyLtv: strategyLtv ?? 0,
        newHealth,
        originalHealth,
        originalCollateralValueUsd,
        newLoanCollateralValueUsd,
        liquidationThreshold,
        originalHealthStatus: getHealthStatus(originalLtv, strategyLtv, liquidationThreshold),
        newHealthStatus: getHealthStatus(newLtv, strategyLtv, liquidationThreshold)
    };

    return result;
}

/**
 * Creates a warning message for transaction that may fail due to insufficient balance
 *
 * @param isAutoSlippage Whether auto-slippage is enabled
 * @param tokenAMetadata Metadata for token A
 * @param tokenBMetadata Metadata for token B
 * @param maxTokenAAmount Maximum amount of token A that may be used
 * @param maxTokenBAmount Maximum amount of token B that may be used
 * @param userTokenAAmount User's available balance of token A
 * @param userTokenBAmount User's available balance of token B
 * @returns Warning message or undefined if no warning is needed
 */
export function getMaxBalanceBalanceWarning(
    isAutoSlippage: boolean,
    tokenAMetadata: TokenListMetadata,
    tokenBMetadata: TokenListMetadata,
    maxTokenAAmount: number | undefined,
    maxTokenBAmount: number | undefined,
    userTokenAAmount: number,
    userTokenBAmount: number
): string | undefined {
    if (maxTokenAAmount && maxTokenAAmount > userTokenAAmount) {
        return `Transaction may fail due to insufficient ${BsMetaUtil.getSymbol(
            tokenAMetadata
        )} balance. Ensure there is ${BsMetaUtil.formatAmount(
            tokenAMetadata,
            maxTokenAAmount
        )} available in your wallet to prevent failures.`;
    }

    if (maxTokenBAmount && maxTokenBAmount > userTokenBAmount) {
        return `Transaction may fail due to insufficient ${BsMetaUtil.getSymbol(
            tokenBMetadata
        )} balance. Ensure there is ${BsMetaUtil.formatAmount(
            tokenBMetadata,
            maxTokenBAmount
        )} available in your wallet to prevent failures.`;
    }

    if (isAutoSlippage) {
        return `Auto-slippage is enabled. Verify the transaction amounts before signing. Transaction may fail due to insufficient balance.`;
    }

    return undefined;
}
